/* eslint-disable array-callback-return */
import React, { useState, useEffect, useRef } from 'react';
import '../view-archer-styles.css';

//material UI
import { Alert, Button, Card, CardHeader, CardContent, Chip, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Link, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material/';
import { blue, red} from '@mui/material/colors';

//third parties:
import { Editor } from '@tinymce/tinymce-react';

//components import
import UploadedFileViewer from './UploadedFileViewer';
import AlertInfo from '../../../dataObjects/AlertInfo';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import ArcherIssue from '../dataObjects/ArcherIssue';
import FlaggedItem from '../dataObjects/FlaggedItem';
import ArcherResponse from '../dataObjects/ArcherResponse';
import flaggedItemGroup from '../dataObjects/FlaggedItemGroup';
import FilerGatewayUploadResponsesDialog from './FilerGatewayUploadResponsesDialog';
import FlaggedItemGroup from '../dataObjects/FlaggedItemGroup';

interface ResponsesProps {
    cpfId: number;
    issue?: ArcherIssue;
}

enum GatewayIssueCategories {
    NotDefined = 0,
    ClarifyExpenditures = 1,
    FormUpload = 2,
    MissingReports = 3,
    LineItemAmendment = 4,
    ExcessContribution = 5,
    Subvendor = 6,
    Acknowledgement = 7,
    AmendmentRequired = 8
}

enum missingFormIssueCodes {
    MissingD103 = 351,
    MissingTreasurer = 38,
    TreasurerResigned = 40,
    BankClosedAccount = 5,
    Escheatment = 366
}

enum ArcherUiResponseType {
    NotSet = 0,
    GatewayClarification = 1,
    GatewayRefundCheckUpload = 6,
    GatewaySupportingDocument = 7,
    GatewayFormUpload = 5,
    ReportItemAmendment = 2,
    ReportItem = 12,
    LinkedEfiledReport = 13,

    //Files:
    ArcherFormUpload = 15,
    ArcherRefundCheckUpload = 16,
    AuditResponsePdf = 17,

    PrfProcessedForm = 20,
    PrfPaperOrganization = 21,
    PrfOnlineOrganization = 22,
    PrfReportItem = 23,
    PrfReport = 24,
    PrfAmendment = 25,
    PrfRefundExpenditure = 26
}

enum ResponseFormType {
    Organization = 1,
    D103 = 2,
    D104 = 3,
    D103or104 = 4,
    T101 = 5
}


const FilerGatewayResponses = (props: ResponsesProps) => {

    useEffect(() => {
        if (props.issue) {
            let issues: ArcherIssue[] = new Array();
            issues.push(props.issue);
            setOutstandingIssues(issues);
            setLoadingIndicator('hidden');
            setCardWidth(12);
        }
        else {
            getGatewayPendingResponses();
        }
        
    }, [])

    /*response manager: 11/12 width; issue editor: full width*/
    const [cardWidth, setCardWidth] = useState(11);

    /* FILE VIEWER */
    const [isFileViewerDialogOpen, setFileViewerDialogOpen] = useState(false);
    const [fileUrl, setFileUrl] = useState('');
    const [currentResponseId, setCurrentResponseId] = useState(0);
    const openFileViewerDialog = (response: any) => {
        setFileUrl('/Archer3/uploads?fileName=' + response.uploadedFileName);
        setFileViewerDialogOpen(true);
        setCurrentResponseId(response.id);
    };

    const closeFileViewerDialog = () => {
        setFileViewerDialogOpen(false);
    };

    /*UPLOAD FILE*/
    const uploadResponse = (event: React.FormEvent<HTMLFormElement>, issue: ArcherIssue) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);

        let formType = 0;
        switch (issue.issueTypeCode) {
            case missingFormIssueCodes.MissingD103:
                formType = ResponseFormType.D103;
                break;
            case missingFormIssueCodes.TreasurerResigned:
            case missingFormIssueCodes.MissingTreasurer:
                formType = ResponseFormType.T101;
                break;
            case missingFormIssueCodes.Escheatment:
            case missingFormIssueCodes.BankClosedAccount:
                formType = ResponseFormType.D103or104;
                break;
        }

        formData.append("formType", formType.toString());
        
        fetch('/archer3/UploadResponse', { method: 'POST', body: formData })
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: UploadResponse',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let info = {
                        type: 'success',
                        title: 'New File Uploaded successfully',
                        errorMessage: 'Please review and process the new file to resolve the issue!',
                        secondaryErrorMessage: ''
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                    getGatewayPendingResponses();
                }
            });
    }
    
    /*SELECT OR UPLOAD RESPONSE*/
    const [isUploadResponsesDialogOpen, setUploadResponsesDialogOpen] = useState(false);
    const [selectedFlaggedItem, setSelectedFlaggedItem] = useState<FlaggedItem>();
    const [selectedFlaggedItemGroup, setSelectedFlaggedItemGroup] = useState<FlaggedItemGroup>();
    const [selectedArcherIssue, setSelectedArcherIssue] = useState<ArcherIssue>();
    const openUploadResponsesDialog = (item: FlaggedItem) => {
        setSelectedFlaggedItem(item);
        setUploadResponsesDialogOpen(true);
    };
    const openUploadResponsesForGroupDialog = (group: FlaggedItemGroup) => {
        setSelectedFlaggedItemGroup(group);
        setUploadResponsesDialogOpen(true);
    };
    const openUploadResponsesForIssueDialog = (issue: ArcherIssue) => {
        setSelectedArcherIssue(issue);
        setUploadResponsesDialogOpen(true);
    };
    const closeUploadResponsesDialog = () => {
        setUploadResponsesDialogOpen(false);
        if (!props.issue) {
            getGatewayPendingResponses();
        }
        else {
            getIssue();
        }
    };

    //loading indicator
    const [isLoading, setLoadingIndicator] = useState('');

    /*Fetch data: pending responses*/
    const [outstandingIssues, setOutstandingIssues] = useState<ArcherIssue[]>();

    const getGatewayPendingResponses = async () => {
        await fetch(`/Archer3/GetOutstandingIssuesWithResponses?cpfId=${props.cpfId}`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetOutstandingIssues',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let issues = result.result;
                    let filteredIssues = issues.filter((issue: ArcherIssue) => issue.gatewayIssueCategoryId>=0 && issue.gatewayIssueCategoryId !==7);
                    setLoadingIndicator('hidden');
                    setOutstandingIssues(filteredIssues);
                    setCardWidth(11);
                }
        })
    }

    const displayResponseStatus = (status: string, message: string = '') => {
        switch (status) {
            case 'Pending':
                return (
                    <Chip label={status} sx={{backgroundColor: 'orange', color: 'white'}} size="small" />
                );
            case 'Resolved':
                return (
                    <Chip label={status} color="success" size="small" />
                );
            case 'Punted':
                return (
                    <Chip label={status} color="primary" size="small" />
                );
            case 'Awaiting Refund':
                return (
                    <Chip label={status} color="primary" size="small" />
                );
            default:
                if (message === 'No Refund' || message === 'Refunded contribution')
                    return (<Chip label={message} color="primary" size="small" />);
                return (<Chip label={status} color="error" size="small" />);
        }
    };

    const getIssue = async () => {
        let url = `/Archer3/GetIssue?issueId=${props.issue?.issueId}`;
        await fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetIssue',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let data = result.result;
                    let issues: ArcherIssue[] = new Array();
                    issues.push(data);
                    setOutstandingIssues(issues);
                }
            })
    }

    /*ACCEPT/REJECT/DISMISS RESPONSES*/
    const [emailNote, setEmailNote] = useState('');
    const [emailSubject, setEmailSubject] = useState('A message from your OCPF Auditor');
    const [isActionDialogOpen, setActionDialogOpen] = useState(false);
    const [actionType, setActionType] = useState('');
    const [selectedIssueId, setSelectedIssueId] = useState(0);
    const [selectedResponseIds, setSelectedResponseIds] = useState<number[]>([]);
    const [selectedFlaggedItemIds, setSelectedFlaggedItemIds] = useState<number[]>([]);
    const [notifyFiler, setNotifyFiler] = useState(true);

    /*TEXT EDITOR SET UP*/
    const editorAPIKey = process.env.REACT_APP_EDITOR_API_KEY;
    const editorBodyRef = useRef<any>(null);

    function handleResponseCheckbox(event: any, responseId: number) {

        if (event.target.checked && responseId === 0) {
            let info = {
                type: 'error',
                title: `Error: No responses submitted`,
                errorMessage: 'There is no response provided for this item!',
                secondaryErrorMessage: ''
            };
            setAlertInfo(info);
            setAlertInfoDialog(true);
        }
        else {
            if (event.target.checked) {
                addResponseToList(responseId);
            }
            else {
                removeResponseFromList(responseId);
            }
        }
    }

    const addResponseToList = (responseId: number) => {
        if (responseId > 0) {
            setSelectedResponseIds([...selectedResponseIds, responseId]);
        }
    }

    const removeResponseFromList = (responseId: number) => {
        if (responseId > 0) {
            setSelectedResponseIds(selectedResponseIds.filter(id => id !== responseId));
        }
    }

    const dismissSuggestedResponse = (response:ArcherResponse) => {
        let url = `/Archer3/DismissSuggestedResponse?currentResponseId=${response.id}&issueId=${response.issueId}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: dismissSuggestedResponse',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    if (!props.issue) {
                        getGatewayPendingResponses();
                    }
                    else {
                        getIssue();
                    }
                }
            })
    }

    /*PUNT FLAGGED ITEM*/
    const puntFlaggedItem = (flaggedItemId: number) => {
        let url = `/Archer3/puntFlaggedItem?FlaggedItemId=${flaggedItemId}`;
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: puntFlaggedItem',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    getGatewayPendingResponses();
                }
            })
    }

    const openIssueActionDialog = (type: string, issueId: number) => {

        if (selectedResponseIds.length === 0) {
            let info = {
                type: 'error',
                title: `Error: No response(s) selected`,
                errorMessage: 'Please select responses first, or wait the response to come in, before accepting or rejecting responses',
                secondaryErrorMessage: ''
            };
            setAlertInfo(info);
            setAlertInfoDialog(true);
        }
        else {
            getEmailResponseToFiler(issueId, type);
        }
    };

    const getEmailResponseToFiler = (issueId: number, type: string) => {

        let isAccepted = type == 'Accept';

        var params = [
            `issueId=${issueId}`,
            `isAccepted=${isAccepted}`
        ].join('&');

        //accept, reject
        let url = `/Archer3/GetEmailResponseToFiler?${params}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: `Error: getEmailResponseToFiler`,
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let emailBody = result.result;
                    setActionDialogOpen(true);
                    setActionType(type);
                    setSelectedIssueId(issueId);
                    setEmailNote(emailBody);
                }
            })
    }

    const closeIssueActionDialog = () => {
        setActionDialogOpen(false);
        setSelectedResponseIds([]);
        setEmailNote("");
    };

    const acceptIssueResponses = (issueId: number, isPartial: boolean) => {
        if (selectedResponseIds.length === 0) {
            let info = {
                type: 'error',
                title: `Error: No response(s) selected`,
                errorMessage: 'Please select responses first, or wait the responses to come in, before accepting the responses',
                secondaryErrorMessage: ''
            };
            setAlertInfo(info);
            setAlertInfoDialog(true);
            return;
        }
        setSelectedIssueId(issueId);
        let url = `/Archer3/AcceptResponses`
        fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(
                {
                    issueId: issueId,
                    emailBody: '',
                    emailSubject: '',
                    sendEmail: false,
                    responseIds: selectedResponseIds.toString(),
                    isPartialResponse: isPartial
                })
        }).then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: `Error: AcceptResponses`,
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setSelectedIssueId(0);
                    setSelectedResponseIds([]);
                    setSelectedFlaggedItemIds([]);
                    closeIssueActionDialog();
                    if (!props.issue) {
                        getGatewayPendingResponses();
                    }
                    else {
                        getIssue();
                    }
                }
            })
    }

    const rejectIssueResponses = () => {

        let emailBody = '';

        if (editorBodyRef.current) {
            emailBody = editorBodyRef.current.getContent();
        }

        let url = `/Archer3/RejectResponses`
        fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(
                {
                    issueId: selectedIssueId,
                    emailBody: emailBody,
                    emailSubject: emailSubject,
                    sendEmail: notifyFiler,
                    responseIds: selectedResponseIds.toString()
                })
        }).then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: `Error: ${actionType}Responses`,
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setSelectedIssueId(0);
                    setSelectedResponseIds([]);
                    setSelectedFlaggedItemIds([]);
                    closeIssueActionDialog();
                    getGatewayPendingResponses();
                }
            })
    };

    //revert issue
    const [confirmActionData, setConfirmationAction] = useState({
        type: '',
        id: 0
    })

    const confirmRevertIssue = (issueId: number) => {
        let info = {
            type: 'warning',
            title: 'Warning: Revert Resolved Issue to Unresolved',
            buttonLabel: "Revert",
            errorMessage: 'Are you sure you would like to revert this issue to unresolved status? Please note that all the accepted responses would be back to unreviewed status.',
            secondaryErrorMessage: ''
        };

        let action = {
            type: 'revertIssue',
            id: issueId
        }
        setConfirmationAction(action);
        setAlertInfo(info);
        setAlertInfoDialog(true);
    }

    const resetIssueResponses = (issueId: number) => {
        var params = [
            `issueId=${issueId}`,
        ].join('&');
        let url = `/Archer3/ResetIssueResponses?${params}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: ResetIssueResponses',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setAlertInfoDialog(false);
                    setConfirmationAction({ type: '', id: 0 });
                    if (!props.issue) {
                        getGatewayPendingResponses();
                    }
                    else {
                        getIssue();
                    };
                }
            })
    }

    const confirmAction = (confirm: boolean) => {
        let type = confirmActionData.type;
        let id = confirmActionData.id;
        if (confirm) {
            switch (type) {
                case 'revertIssue':
                    resetIssueResponses(id);
                    break;
            }
        }
    }

    /*Populate issue content*/
    const populateIssueContent = (issue: ArcherIssue) => {
        switch (issue.gatewayIssueCategoryId) {
            case GatewayIssueCategories.ClarifyExpenditures:
                return populateClarifyExpenditureFlaggedItemsTable(issue);
            case GatewayIssueCategories.MissingReports:
                return populateMissingReportsTable(issue);
            case GatewayIssueCategories.FormUpload:
                return populateMissingFormsTable(issue);
            case GatewayIssueCategories.ExcessContribution:
                return populateExcessContributionsTable(issue);
            case GatewayIssueCategories.Subvendor:
                return populateSubvendorTable(issue);
            case GatewayIssueCategories.AmendmentRequired:
                return populateAmendmentRequiredTable(issue);
            case GatewayIssueCategories.LineItemAmendment:
                return populateReportItemsTable(issue);
            case GatewayIssueCategories.NotDefined:
                return populateGenericResponseTable(issue);
        }
    };

    const populateClarifyExpenditureFlaggedItemsTable = (issue: ArcherIssue) => {

        let flaggedItems = issue.flaggedItems;

        return (
            <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader sx={{ minWidth: 450 }} size="small" aria-label="Flagged Items Table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Status</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Payee, Clarified Payee</TableCell>
                            <TableCell>Purpose, Clarified Purpose</TableCell>
                            <TableCell>Clarified Date</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell>Action</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            flaggedItems &&
                            flaggedItems.map((item: FlaggedItem) => (
                                <TableRow
                                    key={item.flaggedItemId}
                                    className={"flagged-" + item.flaggedItemId}
                                >
                                    <TableCell>
                                        {
                                            displayResponseStatus(item.gatewayStatus)
                                        }
                                    </TableCell>
                                    <TableCell>
                                        <Link href={`https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${item.reportId}`} target="_blank" underline="none">{item.date}</Link>
                                    </TableCell>
                                    <TableCell>
                                        {item.fullName}<br />
                                        <Typography sx={{ color: blue[500] }}><i>{item.clarifiedVendor}</i></Typography>
                                    </TableCell>
                                    <TableCell>
                                        {item.purpose}<br />
                                        <Typography sx={{ color: blue[500] }}><i>{item.clarifiedPurpose}</i></Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Stack spacing={2}>
                                            {
                                                item.responses.length > 0 &&
                                                item.responses.map((response: ArcherResponse) =>
                                                    <Stack direction="row" spacing={1} key={response.id} className={"response-" + response.id} >
                                                        {
                                                            (response.responseType === ArcherUiResponseType.PrfReportItem || response.responseType === ArcherUiResponseType.GatewayClarification) &&
                                                            <Typography variant="body2">{response.responseDate}</Typography>
                                                        }
                                                    </Stack>
                                                )
                                            }
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="right">{item.amount}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            {
                                                item.responses.length === 0 &&
                                                <Typography sx={{ color: red[500] }} variant="body2">No response yet</Typography>
                                            }
                                            {
                                                item.responses.length > 0 &&
                                                item.responses.map((response: ArcherResponse) =>
                                                    <Stack direction="row" spacing={1} key={response.id} className={"response-" + response.id}>
                                                        {
                                                            (response.responseType === ArcherUiResponseType.PrfReportItem || response.responseType === ArcherUiResponseType.GatewayClarification) && response.reviewStatus === 1 &&
                                                             <>
                                                                <Checkbox checked={selectedResponseIds.includes(response.id)} onClick={(event) => handleResponseCheckbox(event, response.id)} />
                                                                <Typography variant="body2"><i>{response.responseTypeDescription}</i></Typography>
                                                             </>
                                                        }
                                                        {
                                                            response.responseType === ArcherUiResponseType.AuditResponsePdf && response.pdfId > 0 &&
                                                            <>
                                                                {
                                                                    response.reviewStatus === 1 &&
                                                                    <Checkbox checked={selectedResponseIds.includes(response.id)} onClick={(event) => handleResponseCheckbox(event, response.id)} />
                                                                }
                                                                <Link sx={{ paddingTop: 2 }} href={response.pdfFileUrl} target="_blank" underline="none">
                                                                    {response.responseDescription}
                                                                </Link>
                                                            </>
                                                        }
                                                        {
                                                            response.responseType === ArcherUiResponseType.AuditResponsePdf && response.uploadedFileUrl.length > 0 &&
                                                            <>
                                                                {
                                                                    response.reviewStatus === 1 &&
                                                                    <Checkbox checked={selectedResponseIds.includes(response.id)} onClick={(event) => handleResponseCheckbox(event, response.id)} />
                                                                }
                                                                <Chip className="is-clickable" variant="outlined" label={`${response.responseTypeDescription}: View File`} color="primary" size="small" onClick={() => openFileViewerDialog(response)} />
                                                            </>
                                                        }
                                                        {
                                                            response.responseType === ArcherUiResponseType.LinkedEfiledReport &&
                                                            <>
                                                                {
                                                                    response.reviewStatus === 1 &&
                                                                    <Checkbox checked={selectedResponseIds.includes(response.id)} onClick={(event) => handleResponseCheckbox(event, response.id)} />
                                                                }
                                                                <Link sx={{ paddingTop: 2 }} href={`https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${response.reportId}`} target="_blank" underline="none">
                                                                    {response.responseDescription}, filed on {response.responseDate}
                                                                </Link>
                                                            </>
                                                        }
                                                    </Stack>
                                                )
                                            }
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            {
                                                item.gatewayStatus !== 'Resolved' &&
                                                <>
                                                    <Button className="is-clickable small-action-button" sx={{minHeight:'40px'}} variant="outlined" size="small" onClick={() => openUploadResponsesDialog(item)}>Add Response</Button>
                                                    <Button className="is-clickable small-action-button" sx={{minHeight: '40px'}} variant="outlined" size="small" color="warning" onClick={() => puntFlaggedItem(item.flaggedItemId)}>Punt</Button>
                                                </>
                                            }
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        )
    };

    const populateMissingReportsTable = (issue: ArcherIssue) => {
        let flaggedItems = issue.flaggedItems;
        let hideNameCSS = issue.issueTypeCode ===100 ? 'hidden' : '';

        return (
            <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader sx={{ minWidth: 450 }} size="small" aria-label="Flagged Items Table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Status</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell className={hideNameCSS}>Name</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell colSpan={2}>Response & Date</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            flaggedItems &&
                            flaggedItems.map((item: FlaggedItem) => (
                                <TableRow
                                    key={item.flaggedItemId}
                                    className={"flagged-" + item.flaggedItemId}
                                >
                                    <TableCell>
                                        {
                                            displayResponseStatus(item.gatewayStatus)
                                        }
                                    </TableCell>
                                    <TableCell>
                                        {item.date}
                                    </TableCell>
                                    <TableCell className={hideNameCSS} >
                                        {item.fullName}
                                    </TableCell>
                                    <TableCell align="right">
                                        {item.amount}
                                    </TableCell>
                                    <TableCell colSpan={2}>
                                        <>
                                        {
                                                item.responses.length > 0 &&
                                                item.responses.map((response: ArcherResponse) =>
                                                    <Stack direction="row" spacing={2} key={response.id} className={"response-" + response.id} >
                                                        {
                                                            response.responseType !== ArcherUiResponseType.AuditResponsePdf &&
                                                            <Link sx={{ width: 450, paddingTop: 2 }} href={`https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${response.reportId}`} target="_blank" underline="none">
                                                                    {response.responseTypeDescription}: {response.responseDescription} - filed on {response.responseDate}
                                                            </Link>
                                                        }
                                                        {
                                                            response.responseType === ArcherUiResponseType.AuditResponsePdf && response.pdfId > 0 &&
                                                            <Link sx={{ width: 450, paddingTop: 2 }} href={response.pdfFileUrl} target="_blank" underline="none">
                                                                {response.responseDescription}
                                                            </Link>
                                                        }
                                                        {
                                                            response.responseType === ArcherUiResponseType.AuditResponsePdf && response.uploadedFileUrl.length > 0 &&
                                                            <Chip className="is-clickable" variant="outlined" label={`${response.responseTypeDescription}: View File`} color="primary" size="small" onClick={() => openFileViewerDialog(response)} />
                                                        }
                                                        {
                                                            item.gatewayStatus !== 'Resolved' && response.reviewStatus === 1 &&
                                                            <>
                                                                <Checkbox checked={selectedResponseIds.includes(response.id)} onClick={(event) => handleResponseCheckbox(event, response.id)} />
                                                                <Chip label="Dismiss" className="is-clickable" variant="outlined" size="small" onClick={() => dismissSuggestedResponse(response)}></Chip>
                                                            </>
                                                        }
                                                    </Stack>
                                            )
                                        }
                                        {
                                            item.responses.length === 0 && item.gatewayStatus !== 'Resolved' &&
                                            <Typography sx={{ color: red[500] }} variant="body2">No response yet</Typography>
                                        }
                                        </>
                                    </TableCell>
                                    <TableCell>
                                        {
                                            item.gatewayStatus !== 'Resolved' && 
                                            <Button className="is-clickable" variant="outlined" size="small" onClick={() => openUploadResponsesDialog(item)}>Add Response</Button>
                                        }
                                    </TableCell>
                                </TableRow>
                            ))
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        )
    };


    //redirect to use the form processor
    const populateMissingFormsTable = (issue: ArcherIssue) => {

        const getRequiredForm = () => {

            switch (issue.issueTypeCode) {
                case missingFormIssueCodes.MissingD103:
                    return (
                        <span><a href="https://ocpf2.blob.core.windows.net/pdf/forms/cpf_d103.pdf" target="_blank">D103</a></span>
                    );
                case missingFormIssueCodes.TreasurerResigned:
                case missingFormIssueCodes.MissingTreasurer:
                    return (
                        <span><a href="https://ocpf2.blob.core.windows.net/pdf/forms/cpft101_2018.pdf" target="_blank">T101</a></span>
                    );
                case missingFormIssueCodes.Escheatment:
                case missingFormIssueCodes.BankClosedAccount:
                    return (
                        <>
                            <span><a href="https://ocpf2.blob.core.windows.net/pdf/forms/cpf_d103.pdf" target="_blank">D103</a></span>
                            <span> or </span><span><a href="https://ocpf2.blob.core.windows.net/pdf/forms/cpf_d104.pdf" target="_blank">D104</a></span>
                        </>
                    )
            }
        }

        return (
            <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader sx={{ minWidth: 450 }} size="small" aria-label="Flagged Items Table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Status</TableCell>
                            <TableCell>Required Form</TableCell>
                            <TableCell>Uploaded Forms</TableCell>
                            <TableCell>Action</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {issue.responses.length > 0 &&
                            issue.responses.map((response: ArcherResponse, index: number) => (
                                <TableRow key={response.id} >
                                    <TableCell>
                                        {
                                            index === 0 && displayResponseStatus(issue.gatewayStatus)
                                        }
                                    </TableCell>
                                    <TableCell>
                                        {
                                            index === 0 &&
                                            getRequiredForm()
                                        }
                                    </TableCell>
                                    <TableCell>
                                        {
                                            (response.responseType === ArcherUiResponseType.ArcherFormUpload ||response.responseType === ArcherUiResponseType.GatewayFormUpload) &&
                                            <Chip className="is-clickable" variant="outlined" label={`${response.responseTypeDescription}(${response.responseDate}): View`} color="primary" size="small" onClick={() => openFileViewerDialog(response)} />

                                        }
                                        {
                                            (response.responseType === ArcherUiResponseType.PrfProcessedForm || response.responseType === ArcherUiResponseType.AuditResponsePdf) &&
                                            <Link sx={{ paddingTop: 2 }} href={response.pdfFileUrl} target="_blank" underline="none">
                                                    {response.responseDescription}
                                            </Link>
                                        }
                                        {
                                            response.responseType === ArcherUiResponseType.LinkedEfiledReport &&
                                            <Link sx={{ paddingTop: 2 }} href={`https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${response.reportId}`} target="_blank" underline="none">
                                                {response.responseDescription}, filed on {response.responseDate}
                                            </Link>
                                        }
                                    </TableCell>
                                    <TableCell>
                                        {
                                            issue.gatewayStatus !== 'Resolved' && response.reviewStatus === 1 &&
                                            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                                                    <Checkbox checked={selectedResponseIds.includes(response.id)} onClick={(event) => handleResponseCheckbox(event, response.id)} />                                                    
                                                    <Chip label="Dismiss" className="is-clickable" variant="outlined" size="small" onClick={() => dismissSuggestedResponse(response)}></Chip>
                                                    <Typography component={Link} variant="body2" href="/apps/forms" target="_blank">Or Use Form Processor</Typography>
                                            </Stack>
                                        }
                                    </TableCell>
                                </TableRow>
                            ))
                        }
                        {
                            issue.responses.length === 0 &&
                            <TableRow
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                                <TableCell>
                                    {
                                            displayResponseStatus(issue.gatewayStatus)
                                    }
                                </TableCell>
                                <TableCell>
                                    {
                                        getRequiredForm()
                                    }
                                </TableCell>
                                <TableCell></TableCell>
                                <TableCell>
                                    <Typography component={Link} variant="body2" href="/apps/forms" target="_blank">Use Form Processor</Typography>
                                </TableCell>
                                    <TableCell></TableCell>
                                    {/*
                                        <TableCell>
                                    <form onSubmit={(event: React.FormEvent<HTMLFormElement>) => uploadResponse(event, issue)}>
                                            <input type="file" name="uploadedFile" />
                                            <Button type="submit" className="is-clickable" variant="text" color="primary" size="small"><Chip variant="outlined" label="Upload Form" color="primary" size="small" /></Button>
                                            <input type="hidden" value={issue.issueId} name="issueId" />
                                            <input type="hidden" value={ArcherUiResponseType.ArcherFormUpload} name="responseType" />
                                     </form>
                                </TableCell>
                                <TableCell>
                                        {
                                            issue.gatewayStatus !== 'Resolved' &&
                                            <Button className="is-clickable" variant="outlined" size="small" onClick={() => openUploadResponsesForIssueDialog(issue)}>Add Other Response</Button>
                                        }
                                </TableCell>
                                    
                                    */}
                                
                            </TableRow>
                        }
                        {/* Auditor can still upload the form if needed 
                            <TableCell>Upload forms:</TableCell>
                                <TableCell>
                                    <form onSubmit={(event: React.FormEvent<HTMLFormElement>) => uploadResponse(event, issue)}>
                                            <input type="file" name="uploadedFile" />
                                            <Button type="submit" className="is-clickable" variant="text" color="primary" size="small"><Chip variant="outlined" label="Upload Form" color="primary" size="small" /></Button>
                                            <input type="hidden" value={issue.issueId} name="issueId" />
                                            <input type="hidden" value={ArcherUiResponseType.ArcherFormUpload} name="responseType" />
                                    </form>
                                </TableCell>
                        
                        */}
                        {
                            issue.gatewayStatus !== 'Resolved' &&
                            <TableRow
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                               <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell>
                                    <Button className="is-clickable" variant="outlined" size="small" onClick={() => openUploadResponsesForIssueDialog(issue)}>Add Other Response</Button>
                                </TableCell>
                            </TableRow>
                        }
                       
                    </TableBody>
                </Table>
            </TableContainer>
        )
    };

    const populateExcessContributionsTable = (issue: ArcherIssue) => {
        let flaggedItems = issue.flaggedItems;
        let flaggedItemGroups = issue.flaggedItemGroups;

        return (
            <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader sx={{ minWidth: 450 }} size="small" aria-label="Flagged Items Table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Status</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell align="right">Amount to Refund</TableCell>
                            <TableCell>Uploaded Checks / Potential Bank Exp. / Updated Report Item </TableCell>
                            <TableCell>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            flaggedItemGroups.length > 0 &&
                            flaggedItemGroups.map((group: flaggedItemGroup) => {
                                return (
                                    <>
                                        {
                                            group.flaggedItems.length > 0 &&
                                            group.flaggedItems.map((item: FlaggedItem, index: number) => (
                                                <TableRow
                                                    key={item.flaggedItemId}
                                                    className={"flagged-" + item.flaggedItemId}
                                                >
                                                    <TableCell>
                                                        {
                                                            index === 0 &&
                                                            displayResponseStatus(group.gatewayStatus)
                                                        }
                                                    </TableCell>
                                                    <TableCell>
                                                        <Link href={`https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${item.reportId}`} target="_blank" underline="none">{item.date}</Link>
                                                    </TableCell>
                                                    <TableCell>
                                                        {item.fullName} {item.groupUi &&
                                                            <div><i>{item.groupUi}</i></div>}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {item.amount}
                                                    </TableCell>
                                                    <TableCell></TableCell>
                                                    <TableCell>
                                                        {
                                                            item.responses
                                                            && item.responses.length > 0
                                                            && (item.responses[0].responseType === ArcherUiResponseType.ReportItemAmendment || item.responses[0].responseType === ArcherUiResponseType.PrfReportItem)
                                                            &&
                                                            <Typography sx={{ color: blue[500], fontSize: 'small'}}>
                                                                    Item Update: {item.responses[0].responseDescription} {item.purpose}
                                                            </Typography>
                                                        }
                                                    </TableCell>
                                                    <TableCell>
                                                        {
                                                            item.responses
                                                            && item.responses.length > 0
                                                            && (item.responses[0].responseType === ArcherUiResponseType.ReportItemAmendment || item.responses[0].responseType === ArcherUiResponseType.PrfReportItem)
                                                            && item.responses[0].reviewStatus === 1 &&
                                                            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                                                                    <Checkbox checked={selectedResponseIds.includes(item.responses[0].id)} onClick={(event) => handleResponseCheckbox(event, item.responses[0].id)} />
                                                                    <Chip label="Dismiss" className="is-clickable" variant="outlined" size="small" onClick={() => dismissSuggestedResponse(item.responses[0])}></Chip>
                                                            </Stack>
                                                        }
                                                    </TableCell>
                                                </TableRow>

                                            ))
                                        }
                                        {
                                            group.responses.length > 0 &&
                                            group.responses.map((response: ArcherResponse, index: number) =>
                                                <>
                                                    {
                                                        (response.responseType === ArcherUiResponseType.GatewayRefundCheckUpload || response.responseType === ArcherUiResponseType.ArcherRefundCheckUpload) &&
                                                        <TableRow key={response.id} >
                                                            <TableCell></TableCell>
                                                            <TableCell><b>{index === 0 && group.groupUi}</b></TableCell>
                                                            <TableCell></TableCell>
                                                            <TableCell></TableCell>
                                                            <TableCell align="right"> {index === 0 && group.amountToRefund}</TableCell>
                                                            <TableCell>
                                                                <Chip className="is-clickable" variant="outlined" label={`${response.responseTypeDescription}: View File`} color="primary" size="small" onClick={() => openFileViewerDialog(response)} />
                                                            </TableCell>
                                                            <TableCell>
                                                                   {
                                                                        group.gatewayStatus !== 'Resolved' && response.reviewStatus === 1 &&
                                                                        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                                                                            <Checkbox checked={selectedResponseIds.includes(response.id)} onClick={(event) => handleResponseCheckbox(event, response.id)} />
                                                                            <Chip label="Dismiss" className="is-clickable" variant="outlined" size="small" onClick={() => dismissSuggestedResponse(response)}></Chip>
                                                                        </Stack>
                                                                  }
                                                            </TableCell>
                                                        </TableRow>
                                                    }
                                                    {
                                                        (response.responseType === ArcherUiResponseType.PrfRefundExpenditure || response.responseType === ArcherUiResponseType.PrfReportItem || response.responseType === ArcherUiResponseType.LinkedEfiledReport || response.responseType === ArcherUiResponseType.AuditResponsePdf) &&
                                                        <TableRow key={response.id} >
                                                            <TableCell></TableCell>
                                                            <TableCell><b>{index === 0 && group.groupUi}</b></TableCell>
                                                            <TableCell></TableCell>
                                                            <TableCell></TableCell>
                                                            <TableCell align="right"> {index === 0 && group.amountToRefund}</TableCell>
                                                            <TableCell>
                                                                    {
                                                                        response.responseType === ArcherUiResponseType.PrfRefundExpenditure &&
                                                                        <Link sx={{ width: 400, paddingTop: 2 }} href={`https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${response.reportId}`} target="_blank" underline="none">
                                                                            Bank Report - Potential Refund Bank Expenditure, filed on {response.responseDate}
                                                                        </Link>
                                                                    }
                                                                    {
                                                                        response.responseType === ArcherUiResponseType.PrfReportItem &&
                                                                        <Link sx={{ width: 400, paddingTop: 2 }} href={`https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${response.reportId}`} target="_blank" underline="none">
                                                                            Deposit Report - Updated Report Item, filed on {response.responseDate}
                                                                        </Link>
                                                                    }
                                                                    {
                                                                        response.responseType === ArcherUiResponseType.LinkedEfiledReport &&
                                                                        <Link sx={{ paddingTop: 2 }} href={`https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${response.reportId}`} target="_blank" underline="none">
                                                                                {response.responseDescription}, filed on {response.responseDate}
                                                                        </Link>
                                                                    }
                                                                    {
                                                                        response.responseType === ArcherUiResponseType.AuditResponsePdf && response.pdfId>0 &&
                                                                        <Link sx={{ paddingTop: 2 }} href={response.pdfFileUrl} target="_blank" underline="none">
                                                                            {response.responseDescription}
                                                                        </Link>
                                                                    }
                                                                    {
                                                                        response.responseType === ArcherUiResponseType.AuditResponsePdf && response.uploadedFileUrl.length > 0 &&
                                                                        <Chip className="is-clickable" variant="outlined" label={`${response.responseTypeDescription}: View File`} color="primary" size="small" onClick={() => openFileViewerDialog(response)} />
                                                                    }
                                                            </TableCell>
                                                            <TableCell>
                                                                    {
                                                                        response.reviewStatus === 1 &&
                                                                        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                                                                            <Checkbox checked={selectedResponseIds.includes(response.id)} onClick={(event) => handleResponseCheckbox(event, response.id)} />
                                                                            <Chip label="Dismiss" className="is-clickable" variant="outlined" size="small" onClick={() => dismissSuggestedResponse(response)}></Chip>
                                                                        </Stack>
                                                                    }
                                                            </TableCell>
                                                        </TableRow>
                                                    }   
                                                </>
                                            )
                                        }
                                        {
                                            <TableRow key={group.groupUi} >
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                                <TableCell>{group.responses.length===0 && group.amountToRefund}</TableCell>
                                                <TableCell>
                                                    {
                                                        group.gatewayStatus !== 'Resolved' &&
                                                        <Button className="is-clickable" variant="outlined" size="small" onClick={() => openUploadResponsesForGroupDialog(group)}>Add Response</Button>
                                                    }
                                                </TableCell>
                                                <TableCell></TableCell>
                                            </TableRow>
                                        }
                                    </>
                                )
                            })
                        }
                        {
                            flaggedItems &&
                            flaggedItems.length > 0 &&
                            flaggedItems.map((item: FlaggedItem) => (
                                <>
                                    <TableRow
                                        key={item.flaggedItemId}
                                        className={"flagged-" + item.flaggedItemId}
                                    >
                                        <TableCell>
                                            {
                                                displayResponseStatus(item.gatewayStatus, item.refundMessage)
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <Link href={`https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${item.reportId}`} target="_blank" underline="none">{item.date}</Link>
                                        </TableCell>
                                        <TableCell>
                                            {item.fullName} {item.groupUi &&
                                                <div><i>{item.groupUi}</i></div>}
                                        </TableCell>
                                        <TableCell align="right">
                                            {item.amount}
                                        </TableCell>
                                        <TableCell>{item.refundMessage}</TableCell>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                    {
                                        item.responses.length > 0 &&
                                        item.responses.map((response: ArcherResponse) =>
                                            <>
                                            {
                                                (response.responseType === ArcherUiResponseType.GatewayRefundCheckUpload || response.responseType === ArcherUiResponseType.ArcherRefundCheckUpload) &&
                                                <TableRow key={response.id} >
                                                    <TableCell></TableCell>
                                                    <TableCell></TableCell>
                                                    <TableCell></TableCell>
                                                    <TableCell></TableCell>
                                                    <TableCell></TableCell>
                                                    <TableCell>
                                                        <Chip className="is-clickable" variant="outlined" label={`${response.responseTypeDescription}: View File`} color="primary" size="small" onClick={() => openFileViewerDialog(response)} />
                                                    </TableCell>
                                                    <TableCell>
                                                        {
                                                            item.gatewayStatus !== 'Resolved' && response.reviewStatus === 1 &&
                                                            <Checkbox checked={selectedResponseIds.includes(response.id)} onClick={(event) => handleResponseCheckbox(event, response.id)} />
                                                        }           
                                                    </TableCell>
                                                </TableRow>
                                            }
                                            {
                                                    (response.responseType === ArcherUiResponseType.PrfRefundExpenditure || response.responseType === ArcherUiResponseType.PrfReportItem || response.responseType === ArcherUiResponseType.LinkedEfiledReport || response.responseType === ArcherUiResponseType.AuditResponsePdf) &&
                                                    <TableRow key={response.id} >
                                                    <TableCell></TableCell>
                                                    <TableCell></TableCell>
                                                    <TableCell></TableCell>
                                                    <TableCell></TableCell>
                                                    <TableCell></TableCell>
                                                    <TableCell>
                                                        {
                                                            response.responseType === ArcherUiResponseType.PrfRefundExpenditure &&
                                                            <Link sx={{ width: 400, paddingTop: 2 }} href={`https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${response.reportId}`} target="_blank" underline="none">
                                                                        Bank Report - Potential Refund Bank Expenditure, filed on {response.responseDate}
                                                                    </Link>       
                                                        }
                                                        {
                                                            response.responseType === ArcherUiResponseType.PrfReportItem &&
                                                            <Link sx={{ width: 400, paddingTop: 2 }} href={`https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${response.reportId}`} target="_blank" underline="none">
                                                                        Deposit Report - Updated Report Item, filed on {response.responseDate}
                                                                    </Link>
                                                         }
                                                        {
                                                             response.responseType === ArcherUiResponseType.LinkedEfiledReport &&
                                                             <Link sx={{ paddingTop: 2 }} href={`https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${response.reportId}`} target="_blank" underline="none">
                                                                        {response.responseDescription}, filed on {response.responseDate}
                                                             </Link>
                                                        }
                                                        {
                                                             response.responseType === ArcherUiResponseType.AuditResponsePdf && response.pdfId>0 &&
                                                             <Link sx={{ paddingTop: 2 }} href={response.pdfFileUrl} target="_blank" underline="none">
                                                                        {response.responseDescription}
                                                                    </Link>
                                                        }
                                                        {
                                                            response.responseType === ArcherUiResponseType.AuditResponsePdf && response.uploadedFileUrl.length > 0 &&
                                                            <Chip className="is-clickable" variant="outlined" label={`${response.responseTypeDescription}: View File`} color="primary" size="small" onClick={() => openFileViewerDialog(response)} />
                                                        }                              
                                                    </TableCell>
                                                    <TableCell>
                                                        {
                                                            item.gatewayStatus !== 'Resolved' && response.reviewStatus === 1 &&
                                                            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                                                                <Checkbox checked={selectedResponseIds.includes(response.id)} onClick={(event) => handleResponseCheckbox(event, response.id)} />
                                                                <Chip label="Dismiss" className="is-clickable" variant="outlined" size="small" onClick={() => dismissSuggestedResponse(response)}></Chip>
                                                            </Stack>
                                                        }                                              
                                                    </TableCell>
                                                </TableRow>
                                            }
                                        </>
                                        )
                                    }
                                    {
                                        <TableRow>
                                            <TableCell></TableCell>
                                            <TableCell></TableCell>
                                            <TableCell></TableCell>
                                            <TableCell></TableCell>
                                            <TableCell></TableCell>
                                            <TableCell>
                                                {
                                                    (item.gatewayStatus !== 'Resolved' && item.refundMessage !== "No Refund") &&
                                                    <Button className="is-clickable" variant="outlined" size="small" onClick={() => openUploadResponsesDialog(item)}>Add Response</Button>
                                                }
                                            </TableCell>
                                            <TableCell></TableCell>
                                        </TableRow>
                                    }
                                </>
                            ))
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        )
    };

    const populateSubvendorTable = (issue: ArcherIssue) => {
        let flaggedItems = issue.flaggedItems;

        return (
            <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader sx={{ minWidth: 450 }} size="small" aria-label="Flagged Items Table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Status</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Payee</TableCell>
                            <TableCell>Purpose</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell colSpan={3}>Response</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            flaggedItems &&
                            flaggedItems.length>0 &&
                            flaggedItems.map((item: FlaggedItem) => {
                                return (
                                    <>
                                        {
                                            <TableRow
                                                key={item.flaggedItemId}
                                                className={"flagged-" + item.flaggedItemId}
                                            >
                                                <TableCell>
                                                    {
                                                        displayResponseStatus(item.gatewayStatus)
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    <Link href={`https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${item.reportId}`} target="_blank" underline="none">{item.date}</Link>
                                                </TableCell>
                                                <TableCell>
                                                    {item.fullName}
                                                </TableCell>
                                                <TableCell>
                                                    {item.purpose} <br />
                                                    <Typography sx={{ color: blue[500] }}><i>{item.clarifiedPurpose}</i></Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {item.amount}
                                                </TableCell>
                                                <TableCell colSpan={3}>
                                                    <>
                                                       {
                                                            item.responses.length > 0 &&
                                                            item.responses.map((response: ArcherResponse) =>
                                                                <Stack direction="row" spacing={2} key={response.id} className={"response-" + response.id} sx={{ alignItems: "center" }}>
                                                                    {
                                                                        (response.responseType === ArcherUiResponseType.LinkedEfiledReport || response.responseType === ArcherUiResponseType.PrfReport) &&
                                                                        <Link sx={{ paddingTop: 2 }} href={`https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${response.reportId}`} target="_blank" underline="none">
                                                                            {response.responseDescription}, filed on {response.responseDate}
                                                                        </Link>
                                                                    }
                                                                    {
                                                                        response.responseType === ArcherUiResponseType.AuditResponsePdf && response.uploadedFileUrl.length > 0 &&
                                                                        <Chip className="is-clickable" variant="outlined" label={`${response.responseTypeDescription}: View File`} color="primary" size="small" onClick={() => openFileViewerDialog(response)} />
                                                                    }
                                                                    {
                                                                        response.responseType === ArcherUiResponseType.AuditResponsePdf && response.pdfId > 0 &&
                                                                        <Link sx={{ paddingTop: 2 }} href={response.pdfFileUrl} target="_blank" underline="none">
                                                                            {response.responseDescription}
                                                                        </Link>
                                                                    }
                                                                    {
                                                                        response.responseType === ArcherUiResponseType.GatewaySupportingDocument &&
                                                                        <Chip className="is-clickable" variant="outlined" label={`${response.responseTypeDescription}: View`} color="primary" size="small" onClick={() => openFileViewerDialog(response)} />
                                                                    }
                                                                    {
                                                                        item.gatewayStatus !== 'Resolved' && response.reviewStatus === 1 &&
                                                                        <Checkbox checked={selectedResponseIds.includes(response.id)} onClick={(event) => handleResponseCheckbox(event, response.id)} />
                                                                    }
                                                                    {
                                                                        item.gatewayStatus !== 'Resolved' && response.reviewStatus === 1 &&
                                                                        <Chip label="Dismiss" className="is-clickable" variant="outlined" size="small" onClick={() => dismissSuggestedResponse(response)}></Chip>
                                                                    }
                                                                </Stack>
                                                            )
                                                        }
                                                        {
                                                            item.responses.length === 0 &&
                                                            <Typography sx={{ color: red[500] }} variant="body2">No response yet</Typography>
                                                        }
                                                    </>
                                                </TableCell>
                                            </TableRow>
                                        }
                                        {
                                            <TableRow>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                    {
                                                        item.gatewayStatus !== 'Resolved' &&
                                                        <Button className="is-clickable" variant="outlined" size="small" onClick={() => openUploadResponsesDialog(item)}>Add Response</Button>
                                                    }
                                                </TableCell>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                            </TableRow>
                                        }
                                    </>
                                )

                            })
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        )
    };

    //re-visit
    const populateAmendmentRequiredTable = (issue: ArcherIssue) => {
        let flaggedItems = issue.flaggedItems;

        return (
            <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader sx={{ minWidth: 450 }} size="small" aria-label="Flagged Items Table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Status</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Purpose</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell>Response</TableCell>
                            <TableCell>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            flaggedItems &&
                            flaggedItems.map((item: FlaggedItem) => {
                                return (
                                    <TableRow
                                        key={item.flaggedItemId}
                                        className={"flagged-" + item.flaggedItemId}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell>
                                            {
                                                displayResponseStatus(item.gatewayStatus)
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <Link href={`https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${item.reportId}`} target="_blank" underline="none">{item.date}</Link>
                                        </TableCell>
                                        <TableCell>
                                            {item.fullName}
                                        </TableCell>
                                        <TableCell>
                                            {item.purpose}
                                        </TableCell>
                                        <TableCell align="right">
                                            {item.amount}
                                        </TableCell>
                                        <TableCell>
                                            {
                                                item.responses.length > 0 &&
                                                item.responses.map((response: ArcherResponse) =>
                                                    <Stack direction="row" spacing={2} key={response.id} className={"response-" + response.id} >
                                                        {
                                                            (response.responseType === ArcherUiResponseType.PrfAmendment || response.responseType === ArcherUiResponseType.LinkedEfiledReport) &&
                                                            <Link sx={{ width: 450, paddingTop: 2 }} href={`https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${response.reportId}`} target="_blank" underline="none">
                                                                {response.responseTypeDescription}: {response.responseDescription} - filed on {response.responseDate}
                                                            </Link>
                                                        }
                                                        {
                                                            item.gatewayStatus !== 'Resolved' && response.reviewStatus === 1 &&
                                                            <>
                                                                <Checkbox checked={selectedResponseIds.includes(response.id)} onClick={(event) => handleResponseCheckbox(event, response.id)} />
                                                                <Chip label="Dismiss" className="is-clickable" variant="outlined" size="small" onClick={() => dismissSuggestedResponse(response)}></Chip>
                                                            </>
                                                        }
                                                    </Stack>
                                                )
                                            }
                                            {
                                                item.responses.length === 0 && item.gatewayStatus !== 'Resolved' &&
                                                <Typography sx={{ color: red[500] }} variant="body2">No response yet</Typography>
                                            }
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {
                                                item.gatewayStatus !== 'Resolved' &&
                                                <Button className="is-clickable" variant="outlined" size="small" onClick={() => openUploadResponsesDialog(item)}>Add Response</Button>
                                            }
                                        </TableCell>
                                    </TableRow>
                                )
                            })
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        )
    }

    const populateReportItemsTable = (issue: ArcherIssue) => {

        let flaggedItems = issue.flaggedItems;

        return (
            <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader sx={{ minWidth: 450 }} size="small" aria-label="Flagged Items Table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Status</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Name/Address</TableCell>
                            <TableCell>Occupation</TableCell>
                            <TableCell>Employer</TableCell>
                            <TableCell>Purpose</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell>Response & Action</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            flaggedItems &&
                            flaggedItems.map((item: FlaggedItem) => (
                                <TableRow
                                    key={item.flaggedItemId}
                                    className={"flagged-" + item.flaggedItemId}
                                >
                                    <TableCell>
                                        {
                                            displayResponseStatus(item.gatewayStatus)
                                        }
                                    </TableCell>
                                    <TableCell>
                                        <Link href={`https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${item.reportId}`} target="_blank" underline="none">{item.date}</Link>
                                    </TableCell>
                                    <TableCell>
                                        {item.recordTypeDescription}
                                    </TableCell>
                                    <TableCell>
                                        <b>{item.fullName}</b>
                                        <div><i>{item.fullAddress}</i></div>
                                    </TableCell>
                                    <TableCell>
                                        {item.occupation}
                                    </TableCell>
                                    <TableCell>
                                        {item.employer}
                                    </TableCell>
                                    <TableCell>
                                        {item.purpose}
                                    </TableCell>
                                    <TableCell align="right">{item.amount}</TableCell>
                                    <TableCell >
                                        <Stack direction="row" spacing={1}>
                                            {
                                                item.responses.length === 0 &&
                                                <Typography sx={{ color: red[500] }} variant="body2">No response yet</Typography>
                                            }
                                        </Stack>
                                        {/*If the auditor uploads the response...*/}
                                        <Stack spacing={1}>
                                            {
                                                item.responses.map((response: ArcherResponse) =>
                                                    <Stack direction="row" spacing={1} >
                                                        {
                                                            item.gatewayStatus !== 'Resolved' && response.reviewStatus === 1 &&
                                                            <>
                                                                <Checkbox checked={selectedResponseIds.includes(response.id)} onClick={(event) => handleResponseCheckbox(event, response.id)} />
                                                            </>
                                                        }
                                                        {
                                                            response.responseType === ArcherUiResponseType.LinkedEfiledReport &&
                                                            <Link sx={{ width: 200, paddingTop: 1 }} href={`https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${response.reportId}`} target="_blank" underline="none">
                                                                Response: {response.responseDescription}, filed on {response.responseDate}
                                                            </Link>
                                                           
                                                        }
                                                        {
                                                            response.responseType === ArcherUiResponseType.PrfReportItem &&
                                                            <Typography sx={{ color: blue[500], fontSize: 'small' }}>
                                                                Item Update
                                                            </Typography>
                                                        }
                                                        {
                                                            response.responseType === ArcherUiResponseType.AuditResponsePdf &&
                                                            <Link sx={{ width: 200, paddingTop: 1 }} href={response.pdfFileUrl} target="_blank" underline="none">
                                                                Response: {response.responseDescription}
                                                            </Link>
                                                        }
                                                        {
                                                            response.responseType === ArcherUiResponseType.ArcherRefundCheckUpload &&
                                                            <Chip className="is-clickable" variant="outlined" label={`${response.responseTypeDescription}: View File`} color="primary" size="small" onClick={() => openFileViewerDialog(response)} />

                                                        }
                                                        {
                                                            item.gatewayStatus !== 'Resolved' &&
                                                            <Chip label="Dismiss" className="is-clickable" variant="outlined" size="small" onClick={() => dismissSuggestedResponse(response)}></Chip>
                                                        }
                                                    </Stack>
                                                )
                                            }
                                        </Stack>
                                    </TableCell>
                                    {/*allow auditor to upload the responses...*/}
                                    <TableCell>
                                        {
                                            item.gatewayStatus !== 'Resolved' &&
                                            <Stack direction="row" spacing={1} sx={{ alignItems: "center"}}>
                                                    <Button className="is-clickable small-action-button" sx={{ minHeight: '40px' }} variant="outlined" size="small" onClick={() => openUploadResponsesDialog(item)}>Add Response</Button>
                                                    <Button className="is-clickable small-action-button" sx={{ minHeight: '40px' }} variant="outlined" color="warning" size="small" onClick={() => puntFlaggedItem(item.flaggedItemId)}>Punt</Button>
                                            </Stack>
                                        }
                                    </TableCell>
                                </TableRow>
                            ))
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        )
    };

    const populateGenericResponseTable = (issue: ArcherIssue) => {

        let flaggedItems = issue.flaggedItems;
        let responses = issue.responses;

        return (
            <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader sx={{ minWidth: 450 }} size="small" aria-label="Flagged Items Table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Status</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Purpose</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell colSpan={3}>Response & Date</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            //item level
                            flaggedItems &&
                            flaggedItems.map((item: FlaggedItem) => {
                                return (
                                    <TableRow
                                        key={item.flaggedItemId}
                                        className={"flagged-" + item.flaggedItemId}
                                    >
                                        <TableCell>
                                            {
                                                displayResponseStatus(item.gatewayStatus)
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <Link href={`https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${item.reportId}`} target="_blank" underline="none">{item.date}</Link>
                                        </TableCell>
                                        <TableCell>
                                            {item.fullName}
                                        </TableCell>
                                        <TableCell>
                                            {item.purpose}
                                        </TableCell>
                                        <TableCell align="right">
                                            {item.amount}
                                        </TableCell>
                                        <TableCell colSpan={3}>
                                            <>
                                                {
                                                    item.responses.length > 0 &&
                                                    item.responses.map((response: ArcherResponse) =>
                                                        <Stack direction="row" spacing={2} key={response.id} className={"response=" + response.id} >
                                                            {
                                                                (response.responseType === ArcherUiResponseType.LinkedEfiledReport || response.responseType === ArcherUiResponseType.PrfReport || response.responseType === ArcherUiResponseType.PrfAmendment) &&
                                                                <Stack direction="row">
                                                                    <Link sx={{ width: 200, paddingTop: 1 }} href={`https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${response.reportId}`} target="_blank" underline="none">
                                                                        Response: {response.responseDescription}, filed on {response.responseDate}
                                                                    </Link>
                                                                </Stack>

                                                            }
                                                            {
                                                                response.responseType === ArcherUiResponseType.AuditResponsePdf &&
                                                                <Stack direction="row">
                                                                    <Link sx={{ width: 200, paddingTop: 1 }} href={response.pdfFileUrl} target="_blank" underline="none">
                                                                        Response: {response.responseDescription}
                                                                    </Link>
                                                                </Stack>
                                                            }
                                                            {
                                                                (response.responseType === ArcherUiResponseType.ArcherRefundCheckUpload) &&
                                                                <Stack direction="row">
                                                                    <Chip className="is-clickable" variant="outlined" label={`${response.responseTypeDescription}: View File`} color="primary" size="small" onClick={() => openFileViewerDialog(response)} />
                                                                </Stack>
                                                            }
                                                            {
                                                                item.gatewayStatus !== 'Resolved' && response.reviewStatus === 1 &&
                                                                <>
                                                                    <Checkbox checked={selectedResponseIds.includes(response.id)} onClick={(event) => handleResponseCheckbox(event, response.id)} />
                                                                    <Chip label="Dismiss" className="is-clickable" variant="outlined" size="small" onClick={() => dismissSuggestedResponse(response)}></Chip>
                                                                </>
                                                            }
                                                        </Stack>
                                                    )
                                                }
                                                {
                                                    item.responses.length === 0 &&
                                                    <Typography sx={{ color: red[500] }} variant="body2">No response yet</Typography>
                                                }
                                            </>
                                        </TableCell>
                                        <TableCell>
                                            {
                                                item.gatewayStatus !== 'Resolved' &&
                                                <Button className="is-clickable" variant="outlined" size="small" onClick={() => openUploadResponsesDialog(item)}>Add Response</Button>
                                            }
                                        </TableCell>
                                    </TableRow>
                                )
                            })
                        }
                        {
                            //issue level
                            responses &&
                            <TableRow>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell align="right"></TableCell>
                                <TableCell colSpan={3}>
                                    <>
                                        {
                                            responses.length > 0 &&
                                            responses.map((response: ArcherResponse) =>
                                                <Stack direction="row" spacing={2} key={response.id} className={"response=" + response.id} >
                                                    {
                                                        (response.responseType === ArcherUiResponseType.LinkedEfiledReport || response.responseType === ArcherUiResponseType.PrfReport) &&
                                                        <Stack direction="row">
                                                            <Link sx={{ width: 200, paddingTop: 1 }} href={`https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${response.reportId}`} target="_blank" underline="none">
                                                                    {response.responseTypeDescription}: {response.responseDescription}, filed on {response.responseDate}
                                                            </Link>
                                                        </Stack>

                                                    }
                                                    {
                                                        response.responseType === ArcherUiResponseType.AuditResponsePdf &&
                                                        <Stack direction="row" sx={{ alignItems: "center" }}>
                                                            <Link sx={{ width: 200, paddingTop: 1 }} href={response.pdfFileUrl} target="_blank" underline="none">
                                                                {response.responseDescription}
                                                            </Link>
                                                        </Stack>
                                                    }
                                                    {
                                                        issue.gatewayStatus !== 'Resolved' && response.reviewStatus === 1 &&
                                                        <>
                                                            <Checkbox checked={selectedResponseIds.includes(response.id)} onClick={(event) => handleResponseCheckbox(event, response.id)} />
                                                            <Chip label="Dismiss" className="is-clickable" variant="outlined" size="small" onClick={() => dismissSuggestedResponse(response)}></Chip>
                                                        </>
                                                    }
                                                </Stack>
                                            )
                                        }
                                        {
                                            responses.length === 0 && issue.gatewayStatus !== 'Resolved' &&
                                            <Typography sx={{ color: red[500] }} variant="body2">No response yet</Typography>
                                        }
                                    </>
                                    </TableCell>
                                    <TableCell>
                                        {
                                            issue.gatewayStatus !== 'Resolved' &&
                                            <Button className="is-clickable" variant="outlined" size="small" onClick={() => openUploadResponsesForIssueDialog(issue)}>Add Response</Button>
                                        }
                                    </TableCell>
                            </TableRow>
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        )
    }

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    return (
        <div className="filer-responses-manager"> 
            <Grid container>
                {
                    outstandingIssues &&
                    outstandingIssues.length === 0 &&
                    <Alert severity="success" sx={{ marginBottom: 2 }}>There are no pending responses for outstanding issues!</Alert>
                }

                {
                    outstandingIssues &&
                    outstandingIssues.length > 0 &&
                    outstandingIssues.map(
                        (issue, index) =>
                            <Grid item xs={cardWidth} key={issue.issueId} sx={{ marginTop: 3 }}>
                                <Card variant="outlined" className={"response-card issue-" + issue.issueId}>
                                    <CardHeader
                                        className="card-header"
                                        title={
                                            <span className="issue-header" title={"issue-" + issue.issueId}>
                                                {issue.issueTypeCode} {issue.header}
                                            </span>
                                        }
                                        action={
                                            <Stack direction="row" spacing={2} sx={{marginRight:1}} >
                                                {
                                                    issue.letterReferences.length > 0 &&
                                                    issue.letterReferences.map((letter, index) =>
                                                        <span className="letter-reference" key={letter.letterId} >
                                                            <Typography variant="body2" sx={{fontSize: 'small' }}><i>{letter.dateSent}-{letter.letterTemplateCode}</i></Typography>
                                                        </span>
                                                    )
                                                }
                                                <Chip label='Revert' variant="outlined" color="warning" size="small" onClick={() => confirmRevertIssue(issue.issueId)} />
                                            </Stack>
                                        }
                                    />
                                    <CardContent className="card-content">
                                        <Grid container className="response-section">
                                            {issue && 
                                                populateIssueContent(issue)
                                            }
                                        </Grid>
                                        {
                                            (issue.gatewayStatus !== 'Resolved' && issue.gatewayStatus !== 'Punted') &&
                                            <Grid container className="response-section" sx={{ marginTop: 2 }}>
                                                <Grid item xs={11} justifyContent="flex-end" sx={{ display: 'flex' }}>
                                                    <Stack direction="row" spacing={2}>
                                                       <Chip label='Accept' variant="outlined" color="success" onClick={() => acceptIssueResponses(issue.issueId, false)} />
                                                       <Chip label='Partial' variant="outlined" color="primary" onClick={() => acceptIssueResponses(issue.issueId, true)} />
                                                       <Chip label='Reject' variant="outlined" color="error" onClick={() => openIssueActionDialog('Reject', issue.issueId)} />
                                                    </Stack>
                                                </Grid>
                                            </Grid>
                                        }
                                    </CardContent>
                                </Card>
                            </Grid>
                    )
                }
            </Grid>
            {/* Accept or Reject responses  */}
            <Dialog
                open={isActionDialogOpen}
                onClose={closeIssueActionDialog}
                maxWidth={'sm'}
                fullWidth={true}
            >
                <DialogTitle>{actionType} Responses...</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <FormControlLabel name="notifyFiler" onChange={()=>setNotifyFiler(!notifyFiler)} control={<Checkbox defaultChecked />} label="Notify Filer?" />
                        <TextField
                            margin="dense"
                            label="Email Subject"
                            rows={1}
                            value={emailSubject}
                            fullWidth
                        />
                        <Editor
                            apiKey={editorAPIKey}
                            onInit={(evt:any, editor:any) => editorBodyRef.current = editor}
                            initialValue={emailNote}
                            init={{
                                height: 500,
                                menubar: true,
                                branding: false,
                                plugins: [
                                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                    'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                                ],
                                toolbar1: 'inserttemplate undo redo | blocks | ' +
                                    'bold italic forecolor fontsize | alignleft aligncenter ' +
                                    'alignright alignjustify | bullist numlist outdent indent | ' +
                                    'removeformat | help | dataBinding | reconcilation',
                                content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                                fontsize_formats: "8pt 10pt 12pt 14pt 18pt 24pt 36pt",
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    {
                        actionType == 'Accept' ? <Button variant="contained" color="success" size="small" onClick={()=>acceptIssueResponses(selectedIssueId,false)}>Accept Responses</Button> : <Button variant="contained" color="error" size="small" onClick={rejectIssueResponses}>Reject Responses</Button>
                    }
                    <Button variant="contained" color="warning" size="small" onClick={closeIssueActionDialog}>Cancel</Button>                    
                </DialogActions>
            </Dialog>
            {/* View uploaded file */}
            <UploadedFileViewer fileUrl={fileUrl} responseId={currentResponseId} isFileViewerDialogOpen={isFileViewerDialogOpen} closeFileViewerDialog={closeFileViewerDialog}></UploadedFileViewer>
            {/*Upload response dialog*/}
            <FilerGatewayUploadResponsesDialog cpfId={props.cpfId} flaggedItem={selectedFlaggedItem} flaggedItemGroup={selectedFlaggedItemGroup} archerIssue={selectedArcherIssue} isUploadResponsesDialogOpen={isUploadResponsesDialogOpen} closeUploadResponsesDialog={closeUploadResponsesDialog}></FilerGatewayUploadResponsesDialog>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog} confirmAction={confirmAction}></AlertInfoDialog>
            }
            {/*loading indicator*/}
            <CircularProgress
                size={50}
                className={isLoading}
                sx={{
                    position: "fixed",
                    left: "50%",
                    top: "40%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 2
                }}
            />
        </div>
    )
}

export default FilerGatewayResponses;